import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { makePaymentSchema } from '@/lib/payments/paymentAccount.schema';
import type { PaymentAccountFormValues, PaymentTab } from '@/lib/payments/paymentAccount.types';

interface UsePaymentAccountFormArgs {
  activeTab: PaymentTab;
  /** Whether the billing sub-form must be filled (no saved address, or the alternate form is open). */
  billingRequired: boolean;
  /** Injectable clock for deterministic expiry bounds in tests. */
  now?: Date;
}

const defaultValues: PaymentAccountFormValues = {
  card: { cardNumber: '', expiry: '', cvv: '', isHsa: false, makeDefault: true, autoPay: true },
  bank: {
    routingNumber: '',
    accountNumber: '',
    bankName: '',
    accountType: 'checking',
    isHsa: false,
    makeDefault: true,
    autoPay: true,
  },
  billing: { name: '', line1: '', line2: '', city: '', state: '', zip: '' },
};

/**
 * The single RHF instance for the Add Payment Account modal. Validates the active tab + billing
 * via a schema chosen at validation time (so one form serves both tabs); zod strips the inactive
 * slice. Shared by Screen 1's test harness and the Phase 6 orchestrator.
 */
export function usePaymentAccountForm({ activeTab, billingRequired, now }: UsePaymentAccountFormArgs) {
  const argsRef = useRef({ activeTab, billingRequired, now });
  argsRef.current = { activeTab, billingRequired, now };

  const resolver = useMemo<Resolver<PaymentAccountFormValues>>(
    () => (values, context, options) => {
      const { activeTab, billingRequired, now } = argsRef.current;
      const schema = makePaymentSchema({ tab: activeTab, billingRequired, now });
      return (zodResolver(schema) as unknown as Resolver<PaymentAccountFormValues>)(values, context, options);
    },
    [],
  );

  return useForm<PaymentAccountFormValues>({ resolver, defaultValues, mode: 'onTouched' });
}
